import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiError, isSessionEnded } from './api/errors.js';

/**
 * Shared TanStack Query configuration.
 *
 * The defaults matter more than they look. An HRMS is read-heavy and its data
 * changes slowly — a holiday calendar published in January is still correct in
 * March — so aggressive refetching costs bandwidth and gains nothing. But
 * retrying a 403 is worse than useless: it delays an error the user needs to
 * see, three times over.
 */
export function createQueryClient(): QueryClient {
  /**
   * A session that ended has to end the *screen* too.
   *
   * The session query answers once, on load, and is not re-asked while
   * somebody sits on a page. So when the idle timeout expired, the next
   * request failed and the page rendered the refusal in place of its
   * contents — "you were signed out after a period of inactivity", inside a
   * shell that still showed the user's name and offered to sign them out.
   *
   * Clearing the session here is what lets the route guard do its job and
   * send them to sign in.
   */
  const endSession = (error: unknown) => {
    if (!isSessionEnded(error)) return;

    // The same ordering as `signOut`, and for the same reason: mark the
    // session while the guards are still subscribed to it, and only then drop
    // everything else. Clearing first tears down the observer, and nothing
    // ever notices the sign-out.
    client.setQueryData(queryKeys.session, null);
    client.removeQueries({
      predicate: (query) => query.queryKey[0] !== queryKeys.session[0],
    });
  };

  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError: endSession }),
    mutationCache: new MutationCache({ onError: endSession }),
    defaultOptions: {
      queries: {
        // Fresh for a minute. Navigating back to a screen within that window
        // is instant instead of showing a spinner for data that has not moved.
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,

        retry(failureCount, error) {
          // A 4xx is an answer, not a failure to get one. Retrying cannot
          // change a permission denial or a validation error.
          if (ApiError.is(error) && error.status < 500) return false;
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8_000),
      },
      mutations: {
        // Never retry a write automatically. A retried "approve leave" that
        // actually succeeded the first time is a duplicate approval.
        retry: false,
      },
    },
  });

  return client;
}

/**
 * Query keys, defined in one place.
 *
 * Centralising them is what makes invalidation reliable: approving leave can
 * invalidate `leave.all()` and know it has caught every list, detail and
 * balance query, rather than hoping every screen used the same string.
 */
export const queryKeys = {
  session: ['session'] as const,
  passkeys: () => ['passkeys'] as const,
  mfa: () => ['mfa'] as const,

  employees: {
    all: () => ['employees'] as const,
    list: (filters: Record<string, unknown>) => ['employees', 'list', filters] as const,
    detail: (id: string) => ['employees', 'detail', id] as const,
    myProfile: () => ['employees', 'me', 'profile'] as const,
    profile: (id: string) => ['employees', 'detail', id, 'profile'] as const,
    designationHistory: () => ['employees', 'me', 'designation-history'] as const,
  },

  calendar: {
    all: () => ['calendar'] as const,
    year: (year: number, includeWithdrawn = false) =>
      ['calendar', 'year', year, includeWithdrawn] as const,
    years: () => ['calendar', 'years'] as const,
    floaters: (year: number) => ['calendar', 'floaters', year] as const,
  },

  directory: {
    all: () => ['directory'] as const,
    list: (filters: Record<string, unknown>) => ['directory', 'list', filters] as const,
    card: (id: string) => ['directory', 'card', id] as const,
    orgChart: () => ['directory', 'org-chart'] as const,
    team: (depth: string) => ['directory', 'team', depth] as const,
  },

  changeRequests: {
    all: () => ['changeRequests'] as const,
    mine: (filters: Record<string, unknown>) => ['changeRequests', 'mine', filters] as const,
    queue: (filters: Record<string, unknown>) => ['changeRequests', 'queue', filters] as const,
  },

  attendance: {
    all: () => ['attendance'] as const,
    month: (employeeId: string, month: string) =>
      ['attendance', employeeId, 'month', month] as const,
    today: () => ['attendance', 'today'] as const,
    team: (date: string) => ['attendance', 'team', date] as const,
    myRegularizations: () => ['attendance', 'regularizations', 'me'] as const,
    pendingRegularizations: () => ['attendance', 'regularizations', 'pending'] as const,
    locations: () => ['attendance', 'locations'] as const,
  },

  leave: {
    all: () => ['leave'] as const,
    balances: () => ['leave', 'balances'] as const,
    ledger: () => ['leave', 'ledger'] as const,
    mine: (filters: Record<string, unknown>) => ['leave', 'mine', filters] as const,
    pending: (status?: string) => ['leave', 'pending', status ?? 'PENDING'] as const,
    history: (employeeId: string) => ['leave', 'history', employeeId] as const,
    calendar: (from: string, to: string) => ['leave', 'calendar', from, to] as const,
    preview: (filters: Record<string, unknown>) => ['leave', 'preview', filters] as const,
  },

  policies: {
    all: () => ['policies'] as const,
    list: (filters: Record<string, unknown>) => ['policies', 'list', filters] as const,
    detail: (policyId: string) => ['policies', 'detail', policyId] as const,
    versions: (policyId: string) => ['policies', 'versions', policyId] as const,
    compliance: (policyId: string) => ['policies', 'compliance', policyId] as const,
    unacknowledged: () => ['policies', 'unacknowledged'] as const,
  },

  posh: {
    all: () => ['posh'] as const,
    mine: () => ['posh', 'mine'] as const,
    cases: (filters: Record<string, unknown>) => ['posh', 'cases', filters] as const,
    detail: (caseId: string) => ['posh', 'detail', caseId] as const,
  },

  announcements: {
    all: () => ['announcements'] as const,
    feed: () => ['announcements', 'feed'] as const,
    admin: (filters: Record<string, unknown>) => ['announcements', 'admin', filters] as const,
    detail: (id: string) => ['announcements', 'detail', id] as const,
    milestones: () => ['announcements', 'milestones'] as const,
  },

  notifications: {
    all: () => ['notifications'] as const,
    list: (filters: Record<string, unknown>) => ['notifications', 'list', filters] as const,
    unread: () => ['notifications', 'unread'] as const,
    preferences: () => ['notifications', 'preferences'] as const,
  },

  locations: {
    all: () => ['locations'] as const,
  },

  admin: {
    all: () => ['admin'] as const,
    console: () => ['admin', 'console'] as const,
    audit: (filters: Record<string, unknown>) => ['admin', 'audit', filters] as const,
    report: (name: string, filters: Record<string, unknown>) =>
      ['admin', 'report', name, filters] as const,
    separations: (filters: Record<string, unknown>) => ['admin', 'separations', filters] as const,
  },
} as const;

export { isSessionEnded };

/**
 * Whether what is on screen belongs to a different request than the one now
 * in flight.
 *
 * Lists that filter or paginate keep the previous rows while the next set
 * loads, so the table does not collapse and jump on every keystroke or tab.
 * The cost is that `isPending` stays **false** throughout — there is data, it
 * is simply the wrong data — so a table wired to `isPending` alone shows the
 * last tab's rows with nothing at all to say that anything is happening.
 *
 * Paired with a table that draws its spinner over the rows rather than
 * replacing them, this keeps the anti-flicker and still admits to loading.
 */
export function isShowingPrevious(query: {
  isPending: boolean;
  isPlaceholderData: boolean;
}): boolean {
  return query.isPending || query.isPlaceholderData;
}
